import { createClient, Session, User } from '@supabase/supabase-js';
import { useState, useEffect, createContext, useCallback } from 'react';
import Logger from '../../utils/logger';
import posthog from 'posthog-js';

// Initialize Supabase client using environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://<project>.supabase.co';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '<your-anon-key>';
export const supabase = createClient(supabaseUrl, supabaseAnonKey); // , { auth: {debug: true} });

// Create context for Supabase auth
export const AuthContext = createContext<{
  session: Session | null;
  isLoading: boolean;
  user: User | null;
}>({
  session: null,
  isLoading: true,
  user: null,
});

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  // Use a stable function to update state in a batched manner
  const updateAuthState = useCallback((newSession: Session | null) => {
    setSession(newSession);
    setUser(newSession?.user || null);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    // Check for existing session
    const checkSession = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        updateAuthState(data.session);
      } catch (error) {
        Logger.error('Error checking auth session', error);
        updateAuthState(null);
      }
    };

    let authSubscription: { unsubscribe: () => void } | null = null;

    // Define auth state change handler
    const setupAuthListener = () => {
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event, updatedSession) => {
        updateAuthState(updatedSession);

        if (!updatedSession || !updatedSession.user) {
          posthog.reset();
        }
      });
      return subscription;
    };

    authSubscription = setupAuthListener();

    // Initial session check and setup listener
    checkSession();

    // Cleanup function
    return () => {
      if (authSubscription) {
        authSubscription.unsubscribe();
      }
    };
  }, [updateAuthState]);

  // Only render children when session is ready
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E34290]"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ session, isLoading, user }}>{children}</AuthContext.Provider>
  );
}
